import { useState } from "react";
import { ImagePlus, X, Smartphone, RefreshCw, BadgeCheck, BadgeAlert, BadgeX } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../lib/api";
import { pwaSupported } from "../lib/pwa";
import { SplashArt, AppIconTile } from "./PwaRuntime";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const Toggle = ({ on, onClick, label, hint, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="flex items-center justify-between w-full border border-border rounded-md px-4 py-3 hover:bg-muted/40 transition-colors">
    <span className="text-left">
      <span className="block text-sm font-bold">{label}</span>
      {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
    </span>
    <span className={`h-6 w-11 rounded-full relative transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-muted"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </span>
  </button>
);

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label className="overline-label">{label}</Label>
    <div className="flex gap-2 items-center">
      <input type="color" data-testid={`${testid}-picker`} value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 rounded-md border border-border cursor-pointer bg-transparent p-1" />
      <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#111111" className="font-mono" />
    </div>
  </div>
);

export default function PwaSettings({ value, onChange, onSave, busy, storeSlug }) {
  const [iconBusy, setIconBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [previewTab, setPreviewTab] = useState("splash");
  const [replay, setReplay] = useState(0);
  const set = (k, v) => onChange({ ...value, [k]: v });

  const complete = !!value.app_name;
  const installable = value.enabled && complete;
  const supported = pwaSupported();

  const upload = async (file, key, setBusy, minSize = 0) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) { toast.error("Use a PNG, JPG, WebP or SVG image"); return; }
    if (minSize) {
      const ok = await new Promise((res) => {
        const img = new Image();
        img.onload = () => res(img.width >= minSize && img.height >= minSize);
        img.onerror = () => res(false);
        img.src = URL.createObjectURL(file);
      });
      if (!ok) { toast.error(`Icon must be at least ${minSize}×${minSize} px`); return; }
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      set(key, data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start" data-testid="pwa-settings">
      <div className="space-y-6">
        {/* Status */}
        <div className="bg-card border border-border rounded-md p-5 flex flex-wrap gap-3" data-testid="pwa-status">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold ${value.enabled ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
            {value.enabled ? <BadgeCheck className="h-3.5 w-3.5" /> : <BadgeX className="h-3.5 w-3.5" />}
            {value.enabled ? "Enabled" : "Disabled"}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold ${installable ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
            {installable ? <BadgeCheck className="h-3.5 w-3.5" /> : <BadgeAlert className="h-3.5 w-3.5" />}
            {installable ? "Installable" : value.enabled ? "Configuration incomplete" : "Not active"}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold ${supported ? "bg-muted text-foreground" : "bg-rose-500/15 text-rose-500"}`} data-testid="pwa-browser-status">
            <Smartphone className="h-3.5 w-3.5" />
            {supported ? "Browser supports app mode" : "Limited support on this browser/device"}
          </span>
          {value.updated_by && <span className="text-xs text-muted-foreground self-center ml-auto" data-testid="pwa-updated-by">Last saved by {value.updated_by}</span>}
        </div>

        {/* Enable */}
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <p className="overline-label">App experience</p>
          <Toggle testid="pwa-enable-toggle" on={value.enabled} onClick={() => set("enabled", !value.enabled)}
            label="Enable App (PWA)" hint="Customers can install your website as an app on their phone or desktop." />
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="overline-label">App name</Label>
              <Input data-testid="pwa-app-name" value={value.app_name} onChange={(e) => set("app_name", e.target.value)} placeholder="My Store" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Short name</Label>
              <Input data-testid="pwa-short-name" value={value.short_name} onChange={(e) => set("short_name", e.target.value)} placeholder="Shown under the icon" /></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">App description</Label>
            <Input data-testid="pwa-description" value={value.description} onChange={(e) => set("description", e.target.value)} placeholder="Shop our latest collection" /></div>
        </div>

        {/* Icon */}
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <p className="overline-label">App icon</p>
          <div className="flex items-center gap-4">
            <AppIconTile pwa={value} slug={storeSlug} size="h-16 w-16" text="text-2xl" />
            <div className="flex gap-2">
              <label data-testid="pwa-icon-upload" className="h-10 px-4 rounded-full border border-border text-xs font-bold flex items-center gap-2 cursor-pointer hover:bg-muted/50 transition-colors">
                <ImagePlus className="h-4 w-4" /> {iconBusy ? "Uploading…" : "Upload icon"}
                <input type="file" accept="image/*" className="hidden" disabled={iconBusy}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "icon", setIconBusy, 192); }} />
              </label>
              {value.icon && (
                <button type="button" data-testid="pwa-icon-remove" onClick={() => set("icon", "")}
                  className="h-10 px-4 rounded-full border border-border text-xs font-bold text-destructive flex items-center gap-1.5 hover:bg-destructive/10 transition-colors">
                  <X className="h-4 w-4" /> Remove
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">PNG/JPG/WebP, minimum 192×192 px (512×512 recommended). If empty, an icon is auto-generated from your business name and theme color — MILTROO branding is never used.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <ColorField label="Theme color" testid="pwa-theme-color" value={value.theme_color} onChange={(v) => set("theme_color", v)} />
            <ColorField label="Background color" testid="pwa-bg-color" value={value.background_color} onChange={(v) => set("background_color", v)} />
          </div>
        </div>

        {/* Splash */}
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <p className="overline-label">Splash screen</p>
          <div className="flex items-center gap-4">
            {value.splash_logo ? <img src={fileUrl(value.splash_logo)} alt="" className="h-14 w-14 rounded-2xl object-cover border border-border" /> : <span className="text-xs text-muted-foreground">No logo — app icon is used</span>}
            <div className="flex gap-2">
              <label data-testid="pwa-splash-logo-upload" className="h-10 px-4 rounded-full border border-border text-xs font-bold flex items-center gap-2 cursor-pointer hover:bg-muted/50 transition-colors">
                <ImagePlus className="h-4 w-4" /> {logoBusy ? "Uploading…" : "Upload splash logo"}
                <input type="file" accept="image/*" className="hidden" disabled={logoBusy}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "splash_logo", setLogoBusy); }} />
              </label>
              {value.splash_logo && (
                <button type="button" data-testid="pwa-splash-logo-remove" onClick={() => set("splash_logo", "")}
                  className="h-10 px-4 rounded-full border border-border text-xs font-bold text-destructive hover:bg-destructive/10 transition-colors">Remove</button>
              )}
            </div>
          </div>
          <ColorField label="Splash background" testid="pwa-splash-bg" value={value.splash_bg} onChange={(v) => set("splash_bg", v)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="overline-label">Loading animation</Label>
              <Select value={value.splash_animation} onValueChange={(v) => set("splash_animation", v)}>
                <SelectTrigger data-testid="pwa-splash-animation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="spinner" data-testid="anim-spinner">Spinner</SelectItem>
                  <SelectItem value="pulse" data-testid="anim-pulse">Pulse</SelectItem>
                  <SelectItem value="dots" data-testid="anim-dots">Bouncing dots</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="overline-label">Duration: {(value.splash_duration_ms / 1000).toFixed(1)}s</Label>
              <input type="range" min="300" max="3000" step="100" data-testid="pwa-splash-duration" value={value.splash_duration_ms}
                onChange={(e) => set("splash_duration_ms", Number(e.target.value))} className="w-full h-10 accent-primary" /></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Loading text (optional)</Label>
            <Input data-testid="pwa-splash-text" value={value.splash_text} onChange={(e) => set("splash_text", e.target.value)} placeholder="Loading your store…" /></div>
        </div>

        {/* Behavior */}
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <p className="overline-label">Behavior</p>
          <div className="space-y-1.5"><Label className="overline-label">App start page</Label>
            <Select value={value.start_page} onValueChange={(v) => set("start_page", v)}>
              <SelectTrigger data-testid="pwa-start-page"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="home" data-testid="start-home">Store home</SelectItem>
                <SelectItem value="cart" data-testid="start-cart">Cart</SelectItem>
                <SelectItem value="account" data-testid="start-account">Customer account</SelectItem>
              </SelectContent>
            </Select></div>
          <Toggle testid="pwa-install-button-toggle" on={value.install_button} onClick={() => set("install_button", !value.install_button)}
            label="Install App button" hint="A small floating install button on supported devices." />
          <Toggle testid="pwa-promo-toggle" on={value.promo_banner} onClick={() => set("promo_banner", !value.promo_banner)}
            label="Install promotion banner" hint='Elegant "Get our app" banner. Disappears for 7 days when dismissed.' />
        </div>

        <Button data-testid="pwa-save-button" onClick={onSave} disabled={busy} className="rounded-full font-bold h-11 px-8">
          {busy ? "Saving…" : "Save App Settings"}
        </Button>
      </div>

      {/* Preview */}
      <div className="bg-card border border-border rounded-md p-6 lg:sticky lg:top-6" data-testid="pwa-preview">
        <div className="flex items-center justify-between mb-4">
          <p className="overline-label">App preview</p>
          <div className="flex gap-1">
            {[["splash", "Splash"], ["home", "Home screen"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`preview-tab-${k}`} onClick={() => setPreviewTab(k)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-extrabold transition-colors ${previewTab === k ? "gradient-bg text-white" : "border border-border"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mx-auto w-64 rounded-[2rem] border-4 border-zinc-800 overflow-hidden shadow-xl">
          <div className="h-6 flex items-center justify-center" style={{ backgroundColor: value.theme_color || "#111" }}>
            <span className="h-1.5 w-16 rounded-full bg-white/40" />
          </div>
          {previewTab === "splash" ? (
            <div key={replay} className="h-96 flex flex-col items-center justify-center relative" style={{ backgroundColor: value.splash_bg || value.background_color || "#fff" }}>
              {value.splash_logo || value.icon ? (
                <img src={fileUrl(value.splash_logo || value.icon)} alt="" className="h-16 w-16 rounded-2xl object-cover shadow-lg" />
              ) : (
                <AppIconTile pwa={value} slug={storeSlug} size="h-16 w-16" text="text-2xl" />
              )}
              <SplashArt animation={value.splash_animation} color={value.theme_color} />
              {value.splash_text && <p className="mt-3 text-[11px] font-bold" style={{ color: value.theme_color }}>{value.splash_text}</p>}
              <p className="absolute bottom-5 text-[9px] font-extrabold tracking-widest uppercase" style={{ color: value.theme_color, opacity: 0.55 }}>{value.app_name || "App"}</p>
            </div>
          ) : (
            <div className="h-96 bg-muted/60 p-5 grid grid-cols-4 gap-4 content-start">
              <div className="flex flex-col items-center gap-1.5">
                <AppIconTile pwa={value} slug={storeSlug} />
                <span className="text-[9px] font-bold text-center leading-tight break-all">{value.short_name || value.app_name || "App"}</span>
              </div>
            </div>
          )}
        </div>
        {previewTab === "splash" && (
          <Button type="button" data-testid="pwa-preview-replay" variant="outline" onClick={() => setReplay((r) => r + 1)} className="rounded-full font-bold text-xs mt-4 w-full">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Replay splash
          </Button>
        )}
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">Preview is indicative. The final splash and install experience depend on the customer's device and browser.</p>
      </div>
    </div>
  );
}
